import React, { useState, useEffect } from 'react';
import { X, Edit2, Check, AlertCircle, Loader2 } from 'lucide-react';

const WorkspaceRenameModal = ({ 
  isOpen, 
  onClose, 
  workspace, 
  onRename, 
  api 
}) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && workspace) {
      setName(workspace.name || '');
      setError('');
    }
  }, [isOpen, workspace]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Workspace name is required');
      return;
    }

    if (name.trim() === workspace?.name) {
      setError('Please enter a different name');
      return;
    }

    if (name.trim().length < 2) {
      setError('Workspace name must be at least 2 characters');
      return;
    }

    if (name.trim().length > 100) {
      setError('Workspace name must be less than 100 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the new workspace rename endpoint
      const response = await api.renameWorkspace(workspace.id, name.trim());
      
      if (response.success) {
        // Call parent callback with updated workspace
        onRename(response.workspace);
        onClose();
      } else {
        setError(response.error || 'Failed to rename workspace');
      }
    } catch (error) {
      console.error('Failed to rename workspace:', error);
      setError(error.message || 'Failed to rename workspace');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit();
    } else if (e.key === 'Escape' && !loading) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Edit2 className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Rename Workspace
              </h3>
              <p className="text-sm text-gray-500">
                Change the name of "{workspace?.name}"
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workspace Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(''); // Clear error on change
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter workspace name..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                error 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
              }`}
              disabled={loading}
              maxLength={100}
              autoFocus
            />
            <div className="mt-1 flex justify-between text-xs text-gray-500">
              <span>{name.length}/100 characters</span>
              {name.trim() && name.trim().length >= 2 && (
                <span className="text-green-600">✓ Valid name</span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Preview */}
          {name.trim() && name.trim() !== workspace?.name && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">Preview:</div>
              <div className="text-sm font-medium text-gray-900">
                {name.trim()}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !name.trim() || name.trim() === workspace?.name || name.trim().length < 2}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Renaming...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Rename Workspace</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceRenameModal;